import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  ChatMessage,
  FollowupOption,
  LeadDetail,
  TestCapturedData,
  TestSessionCreated,
} from "@school-sales-ai/shared";
import { ApiError, api } from "@/lib/api";
import { getChatHistory, openChatStream, sendChatMessage } from "@/lib/chatApi";
import { useLeadDetail } from "@/hooks/useLeadDetail";

export type ScoreDelta = {
  messageId: string;
  delta: number;
  after: number;
};

// What the page tells the hook to drive:
//  - "new"    → create a fresh isTest session on entering the chat view
//  - "resume" → continue an existing isTest lead from the list
export type TestTarget =
  | { mode: "new"; nonce: number }
  | { mode: "resume"; leadId: string; sessionRef: string };

export type UseTestAgentReturn = {
  messages: ChatMessage[];
  isTyping: boolean;
  showInitialReplies: boolean;
  initialOptions: FollowupOption[] | null;
  followupOptions: FollowupOption[];
  score: number;
  scoreHistory: ScoreDelta[];
  sessionRef: string | null;
  leadId: string | null;
  showWhatsappCta: boolean;
  capturedData: TestCapturedData | null;
  send: (content: string) => Promise<void>;
};

export const TEST_QUICK_REPLIES: FollowupOption[] = [
  { label: "¿Qué carreras ofrecen?", prompt: "¿Qué carreras ofrecen?" },
  { label: "¿Cuánto cuesta la colegiatura?", prompt: "¿Cuánto cuesta la colegiatura?" },
  { label: "¿Cuándo inicia el siguiente ciclo?", prompt: "¿Cuándo inicia el siguiente ciclo?" },
];

function mapCaptured(lead: LeadDetail | undefined): TestCapturedData | null {
  if (!lead) return null;
  const fields: Record<string, string> = {};
  if (lead.capturedFields) {
    for (const [k, v] of Object.entries(lead.capturedFields)) {
      if (v != null) fields[k] = String(v);
    }
  }
  // Age and grade collapse into one display string here, the same way
  // ai.service does it for the prompt tail: the renderer should not be the
  // thing deciding that "10 años" and "5to de primaria" are one fact.
  const candidates = (lead.candidates ?? [])
    .filter((c) => c.name !== null)
    .map((c) => ({
      name: c.name ?? "",
      detail:
        [c.gradeLabel, c.age !== null ? `${c.age} años` : null]
          .filter((d): d is string => !!d)
          .join(" · ") || null,
      programName: c.productName,
    }));

  // A program already shown on a person's row is not repeated as a bare
  // "Programa": this panel exists to show what the agent captured, and stating
  // one capture twice makes two children look like four facts. Filtered here
  // rather than in the renderer so the rule lives in one place — the lead
  // detail's interests section applies the same one on its own data.
  //
  // Matched on NAME because that is what this projection carries; the ids are
  // dropped on purpose, and a product name is unique per client.
  const claimedNames = new Set(
    candidates.map((c) => c.programName).filter((n): n is string => n !== null),
  );

  return {
    name: lead.name,
    phone: lead.phone,
    email: lead.email,
    programs: (lead.interests ?? [])
      .filter((i) => candidates.length === 0 || !claimedNames.has(i.productName))
      .map((i) => ({
        name: i.productName,
        isPrimary: i.isPrimary,
      })),
    candidates,
    cycleKey: lead.cycleKey,
    cycleLabel: lead.cycleLabel,
    fields,
  };
}

/**
 * Drives one "Probar Agente" test session on the REAL prod chat flow. Session
 * creation is authed (`POST /crm/test-agent/sessions`); the conversation runs
 * through the public `/chat/message` + `/chat/stream` endpoints by sessionRef,
 * exactly like the widget. Captured data comes from the real lead detail.
 */
export function useTestAgent(target: TestTarget | null): UseTestAgentReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showInitialReplies, setShowInitialReplies] = useState(false);
  const [initialOptions, setInitialOptions] = useState<FollowupOption[] | null>(null);
  const [score, setScore] = useState(0);
  const [scoreHistory, setScoreHistory] = useState<ScoreDelta[]>([]);
  const [sessionRef, setSessionRef] = useState<string | null>(null);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [showWhatsappCta, setShowWhatsappCta] = useState(false);
  const [followupOptions, setFollowupOptions] = useState<FollowupOption[]>([]);

  const queryClient = useQueryClient();
  const streamRef = useRef<EventSource | null>(null);

  // Captured contact/qualification comes from the real lead, refreshed after
  // each turn via the ["lead", leadId] invalidation below.
  const { data: leadDetail } = useLeadDetail(leadId ?? "");
  const capturedData = useMemo(() => mapCaptured(leadDetail), [leadDetail]);

  // `target` is a fresh object each render; key the init effect on a stable
  // string so it runs once per distinct target (not on every re-render, which
  // would cancel the in-flight session creation via the cleanup).
  const targetKey = target
    ? target.mode === "new"
      ? `new:${target.nonce}`
      : `resume:${target.sessionRef}`
    : null;
  const targetRef = useRef(target);
  targetRef.current = target;

  // (Re)initialize whenever the page points us at a new target.
  useEffect(() => {
    const target = targetRef.current;
    if (!target || !targetKey) return;

    streamRef.current?.close();
    streamRef.current = null;
    setMessages([]);
    setIsTyping(false);
    setShowInitialReplies(false);
    setInitialOptions(null);
    setScore(0);
    setScoreHistory([]);
    setShowWhatsappCta(false);
    setFollowupOptions([]);
    setSessionRef(null);
    setLeadId(null);

    let cancelled = false;

    if (target.mode === "new") {
      void (async () => {
        try {
          const created = await api.post<TestSessionCreated>(
            "/crm/test-agent/sessions",
            {},
          );
          if (cancelled) return;
          setSessionRef(created.sessionRef);
          setLeadId(created.leadId);
          if (created.initialOptions && created.initialOptions.length > 0) {
            setInitialOptions(created.initialOptions);
          }
          setMessages(
            created.greeting
              ? [{ id: "greeting", role: "assistant", content: created.greeting }]
              : [],
          );
          setShowInitialReplies(true);
        } catch (err) {
          if (cancelled) return;
          // Show what the backend actually said. During onboarding the failure is
          // usually a missing prerequisite (no deployment yet), and "intenta de
          // nuevo" sends the operator into a loop that can never succeed.
          const detail = err instanceof ApiError ? ` (${err.message})` : "";
          setMessages([
            {
              id: `err-${Date.now()}`,
              role: "assistant",
              content: `No se pudo iniciar la prueba${detail}.`,
            },
          ]);
        }
      })();
    } else {
      const { leadId: resumeLeadId, sessionRef: resumeRef } = target;
      setSessionRef(resumeRef);
      setLeadId(resumeLeadId);
      void (async () => {
        try {
          const history = await getChatHistory(resumeRef);
          if (cancelled) return;
          const seeded = history.greeting
            ? [
                { id: "greeting", role: "assistant" as const, content: history.greeting },
                ...history.messages,
              ]
            : history.messages;
          setMessages(seeded);
          setScore(history.score);
        } catch {
          if (cancelled) return;
          setMessages([
            {
              id: `err-${Date.now()}`,
              role: "assistant",
              content: "No se pudo cargar la conversación de prueba.",
            },
          ]);
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [targetKey]);

  const send = useCallback(
    async (content: string) => {
      if (!sessionRef) return;
      setShowInitialReplies(false);
      setInitialOptions(null);
      setFollowupOptions([]);
      setMessages((prev) => [
        ...prev,
        { id: `user-${Date.now()}`, role: "user", content },
      ]);
      setIsTyping(true);

      const botMsgId = `bot-${Date.now()}`;
      let firstToken = true;
      let settled = false;
      const prevScore = score;

      const appendToken = (token: string): void => {
        if (firstToken) {
          firstToken = false;
          setIsTyping(false);
          setMessages((prev) => [
            ...prev,
            { id: botMsgId, role: "assistant", content: token, isStreaming: true },
          ]);
        } else {
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === botMsgId);
            const msg = prev[idx];
            if (idx === -1 || !msg) return prev;
            return [
              ...prev.slice(0, idx),
              { ...msg, content: msg.content + token },
              ...prev.slice(idx + 1),
            ];
          });
        }
      };

      const finishBotStreaming = (): void => {
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === botMsgId);
          const msg = prev[idx];
          if (idx === -1 || !msg) return prev;
          return [
            ...prev.slice(0, idx),
            { ...msg, isStreaming: false },
            ...prev.slice(idx + 1),
          ];
        });
      };

      const showError = (): void => {
        setIsTyping(false);
        if (firstToken) {
          setMessages((prev) => [
            ...prev,
            {
              id: `err-${Date.now()}`,
              role: "assistant",
              content: "Lo siento, ocurrió un error. Por favor intenta de nuevo.",
            },
          ]);
        } else {
          finishBotStreaming();
        }
      };

      try {
        await sendChatMessage(sessionRef, content);
      } catch {
        showError();
        return;
      }

      streamRef.current?.close();
      const es = openChatStream(sessionRef);
      streamRef.current = es;

      es.addEventListener("token", (e) => {
        const { token } = JSON.parse((e as MessageEvent).data) as { token: string };
        appendToken(token);
      });

      es.addEventListener("text_reset", () => {
        // The model discarded a preliminary answer after a tool call — drop what
        // streamed so far and show the typing indicator until the replacement
        // text arrives.
        firstToken = true;
        setMessages((prev) => prev.filter((m) => m.id !== botMsgId));
        setIsTyping(true);
      });

      es.addEventListener("followup_options", (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data) as {
            options: FollowupOption[];
          };
          setFollowupOptions(data.options);
        } catch {
          // ignore malformed payload
        }
      });

      es.addEventListener("done", (e) => {
        settled = true;
        const data = JSON.parse((e as MessageEvent).data) as {
          messageId: string | null;
          score: number;
          showWhatsappCta: boolean;
          isHandoff: boolean;
        };
        if (data.showWhatsappCta) setShowWhatsappCta(true);
        setScore(data.score);
        setScoreHistory((prev) => [
          ...prev,
          {
            messageId: data.messageId ?? `done-${Date.now()}`,
            delta: data.score - prevScore,
            after: data.score,
          },
        ]);
        finishBotStreaming();
        setIsTyping(false);
        es.close();
        streamRef.current = null;
        if (leadId) {
          void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
          // The cart and the quote request change mid-conversation, and the debug
          // panel shows them; their queries live under their own roots.
          void queryClient.invalidateQueries({ queryKey: ["orders"] });
          void queryClient.invalidateQueries({ queryKey: ["quotes"] });
        }
        void queryClient.invalidateQueries({ queryKey: ["test-sessions"] });
      });

      es.addEventListener("error", (e) => {
        // Named backend error frame carries a payload; the native close-error
        // (fired after a normal "done") does not — ignore the latter.
        const data = (e as MessageEvent).data as string | undefined;
        if (settled && !data) {
          es.close();
          streamRef.current = null;
          return;
        }
        settled = true;
        showError();
        es.close();
        streamRef.current = null;
      });
    },
    [sessionRef, leadId, score, queryClient],
  );

  // Close any open stream on unmount.
  useEffect(() => {
    return () => {
      streamRef.current?.close();
      streamRef.current = null;
    };
  }, []);

  return {
    messages,
    isTyping,
    showInitialReplies,
    initialOptions,
    followupOptions,
    score,
    scoreHistory,
    sessionRef,
    leadId,
    showWhatsappCta,
    capturedData,
    send,
  };
}
